import React, { useMemo, useState } from 'react';
import { notification, Typography } from 'antd';
import { useQuery } from 'react-query';
import {
  AssetListSearchResponse,
  getAssetListByAnyRef,
  GetAssetListByAnyRefParams,
} from '../../services/assets';

import { getFinAssetListByAnyRef } from '../../services/finassets';

import { toCurrency } from 'utils/utils';
import DataGrid from 'components/DataGrid';

import ParListSelect from 'components/ParListSelect';
import { DataGridColDef, DataGridProps } from 'components/DataGrid/DataGrid';

import DF from 'DisplayFramework';

type AssetListProps = Pick<DataGridProps<any>, 'rowSelection'> & {
  phase: 'PROPOSAL' | 'PROD';
  status?: string;
  onSelect?: (item: AssetListSearchResponse) => void;
  ctref?: string;
};

const AssetListModalV = (props: AssetListProps) => {
  const [searchParams, setSearchParams] = useState<GetAssetListByAnyRefParams>({
    assetreference: '',
    assetlabel: '',
    clientname: '',
    clientreference: '',
    ctreference: props.ctref??'',
    deliveryreference: '',
    orderreference: '',
    propreference: '',
    refsupplier: '',
    suppliername: '',
    status: props.status,
    chassisno: '',
    plateno: ''
  });
  const [searchs, setsearchs] = useState(false);
  const { onSelect } = props;

  const { data: assets, isFetching } = useAssets(
    searchParams,
    props.phase,
    searchs
  );
  const [datasource, setdatasource] = useState<any>([]);
  const columns = useMemo<DataGridColDef<AssetListSearchResponse>[]>(
    () => [
      {
        dataIndex: 'assetreference',
        render: (ctreference, record) => {
          if (onSelect) {
            return (
              <Typography.Link onClick={() => onSelect(record)}>
                {ctreference}
              </Typography.Link>
            );
          } else return { ctreference };
        },
        filtrable: true,
        fixed: 'left',
        width: 150,
      },
      {
        dataIndex: 'ctreference',
        filtrable: true,
        width: 150,
      },

      {
        dataIndex: 'assetlabel',
        width: 150,
      },
      {
        dataIndex: 'status',
        filtrable: true,
        filterComponent: (
          <ParListSelect parcode="ASSETSTAT" style={{ width: '100%' }} />
        ),
        render: (val, record) => record.statuslabel,

        width: 120,
      },
      {
        dataIndex: 'refsupplier',
        filtrable: true,
        width: 150,
      },
      {
        dataIndex: 'suppliername',
        filtrable: true,
        width: 150,
      },
      {
        dataIndex: 'clientreference',
        filtrable: true,
        width: 150,
      },
      {
        dataIndex: 'clientname',
        filtrable: true,
        width: 150,
      },

      {
        dataIndex: 'assettypelabel',
        width: 150,
      },
      {
        dataIndex: 'assetcategorylabel',
        width: 200,
      },

      {
        dataIndex: 'assetorderreference',
        width: 100,
      },
      {
        dataIndex: 'assetdeliveryreference',
        width: 100,
      },

      {
        dataIndex: 'assetgrossamount',
        width: 150,
        align: 'right',
        render: (value, record) => toCurrency(value, record.currcode),
      },
      {
        dataIndex: 'assetnetamount',
        align: 'right',
        width: 150,
        fixed: 'right',
        render: (value, record) => toCurrency(value, record.currcode),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <DF.Provider pagcode="assetlist">
      <DataGrid
        scroll={{ x: 1500, y: 400 }}
        titleComcode={
          props.phase === 'PROD' ? 'pageTitleProd' : 'pageTitleProposal'
        }
        dataSource={searchs === false ? datasource : assets}
        rowKey={(record) => record.assetid}
        columns={columns}
        hideExtraAction={!!onSelect}
        initialSearchValues={searchParams}
        onSearch={(values) => {
          setSearchParams({
            ...values,
          });
          setdatasource(assets);
          setsearchs(true);
        }}
        isLoading={isFetching}
        rowSelection={props.rowSelection}
      />
    </DF.Provider>
  );
};

const useAssets = (searchParams: any, assetPhase: string, searchs: boolean) => {
  const assetsQuery = useQuery(
    ['getAssetListByAnyRef', searchParams],
    () =>
      getAssetListByAnyRef(searchParams)
        .then((res) => {
          return res.list || [];
        })
        .catch((error) => {
          notification.error({
            description: error.message,
            message: 'Asset Service is not available',
          });
          return [];
        }),
    {
      refetchOnWindowFocus: false,
      enabled: assetPhase === 'PROPOSAL' && searchs,
    }
  );
  const finAssetsQuery = useQuery(
    ['getFinAssetListByAnyRef', searchParams],
    () =>
      getFinAssetListByAnyRef(searchParams).then((res) => {
        return res.list || [];
      }),
    { refetchOnWindowFocus: false, enabled: assetPhase === 'PROD' && searchs }
  );

  return assetPhase === 'PROPOSAL' ? assetsQuery : finAssetsQuery;
};

export default AssetListModalV;
